import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { add } from 'date-fns';
import { WIZARD_STATUS_COMPLETE } from 'platform/site-wide/wizard';

import { formatDate } from '../../../shared/utils/dates';
import { BDD_INFO_URL, DISABILITY_526_V2_ROOT_URL } from '../../constants';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import SubTaskContainerHeader from '../SubTaskContainerHeader';
import useSubtaskStatusMarker from '../useSubtaskStatusMarker';

import pageNames from './pageNames';
import { getDiffInDays } from '../utils';
import {
  trackSubtaskStepView,
  trackFileClaimLinkClick,
  trackExternalLinkClick,
  trackBddEligibilityDisplayed,
} from '../../utils/tracking/features/subtaskTracking';

const FileBDDClaim = ({ data = {} }) => {
  const { addStep, getHistory } = usePathHistory();
  const { radDate } = data;

  useSubtaskStatusMarker(WIZARD_STATUS_COMPLETE);

  const differenceBetweenDatesInDays = getDiffInDays(radDate) + 1;

  const daysRemainingToFileBDD = differenceBetweenDatesInDays - 90;
  const isLastDayToFileBDD = daysRemainingToFileBDD === 0;
  const today = new Date();

  const dateOfLastBDDEligibility = formatDate(
    add(today, { days: daysRemainingToFileBDD }),
  );
  const daysLeft = `day${daysRemainingToFileBDD > 1 ? 's' : ''} left`;

  const bddCTALabel = 'File a disability claim online';

  // ──────────────────────────────────────────────
  // STEP VIEW TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    trackSubtaskStepView({
      stepName: pageNames.fileBDD,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ──────────────────────────────────────────────
  // BDD-SPECIFIC TRACKING
  // ──────────────────────────────────────────────
  useEffect(() => {
    trackBddEligibilityDisplayed({
      daysRemaining: daysRemainingToFileBDD,
      isLastDay: isLastDayToFileBDD,
    });
  }, [daysRemainingToFileBDD, isLastDayToFileBDD]);

  return (
    <>
      <SubTaskContainerHeader title="File your disability claim online" />
      <div className="vads-u-margin-top--3 vads-u-margin-bottom--3">
        <va-alert status="success">
          <h3 slot="headline">
            You qualify for the Benefits Delivery at Discharge (BDD) program
          </h3>
          {daysRemainingToFileBDD < 0 ? null : (
            <p className="vads-u-margin-top--0">
              Based on your separation date,{' '}
              {isLastDayToFileBDD ? (
                <>
                  this is your <b>last day</b>
                </>
              ) : (
                <>
                  you have <b>{daysRemainingToFileBDD}</b> {daysLeft}
                </>
              )}{' '}
              to file a BDD claim (also known as a “pre-discharge claim”) using
              VA Form 21-526EZ. You have until{' '}
              <strong>
                {isLastDayToFileBDD ? '' : `${dateOfLastBDDEligibility} at`}
                {' 11:59 p.m. ET'}
              </strong>{' '}
              to complete your claim.
            </p>
          )}
          <p>
            You can still file for disability compensation even if you don’t
            file the BDD claim on time. It’ll automatically become a standard
            disability compensation claim once you submit it.
          </p>
          <va-link
            external
            id="learn_about_bdd"
            className="vads-u-margin-bottom--0"
            href={BDD_INFO_URL}
            text="Learn more about the pre-discharge claim"
            onClick={() =>
              trackExternalLinkClick({
                linkLabel: 'Learn more about the pre-discharge claim',
                pageName: pageNames.fileBDD,
                pathHistoryData: getHistory(),
              })
            }
          />
        </va-alert>
        <p>
          You can file a pre-discharge claim through the Benefits Delivery at
          Discharge (BDD) program if you’re separating from the military in the
          next 180 to 90 days.
        </p>
        <va-link-action
          href={`${DISABILITY_526_V2_ROOT_URL}/introduction`}
          onClick={() => {
            trackFileClaimLinkClick({
              linkLabel: bddCTALabel,
              pageName: pageNames.fileBDD,
              pathHistoryData: getHistory(),
            });
          }}
          text={bddCTALabel}
          type="primary"
        />
      </div>
    </>
  );
};

FileBDDClaim.propTypes = {
  data: PropTypes.shape({}),
};

export default {
  name: pageNames.fileBDD,
  component: FileBDDClaim,
  back: pageNames.rad,
  next: null,
};
